// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';

// material ui
import { Box, IconButton, Tooltip, Typography } from '@mui/material';

// shared components
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';

const formatMetaData = (metaData) => {
  if (metaData == null) {
    return '';
  }

  if (typeof metaData === 'string') {
    const trimmed = metaData.trim();

    if (!trimmed) {
      return '';
    }

    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return trimmed;
    }
  }

  try {
    return JSON.stringify(metaData, null, 2);
  } catch {
    return String(metaData);
  }
};

const AlarmMetaDataDrawer = ({ alarmType, alarmSubtype, metaData }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const formattedMetaData = formatMetaData(metaData);
  const hasMetaData = Boolean(formattedMetaData);

  if (!hasMetaData) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={t('actions.viewItem', { item: 'metadata' })}
        placement="top"
      >
        <Box component="span">
          <IconButton
            onClick={() => {
              if (hasMetaData) {
                setOpen(true);
              }
            }}
            size="small"
            disabled={!hasMetaData}
            aria-label={t('actions.viewItem', { item: 'metadata' })}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
              '&:hover .data-icon': {
                color: 'primary.main',
              },
            }}
          >
            <DataObjectRoundedIcon className="data-icon" />
          </IconButton>
        </Box>
      </Tooltip>

      <InsetDrawer
        title={t(`type.${alarmType}.subtype.${alarmSubtype}.drawer.title`, {
          ns: 'alarms',
          defaultValue: alarmType,
        })}
        open={open && hasMetaData}
        onClose={() => setOpen(false)}
      >
        {hasMetaData ? (
          <Box component="pre" sx={{ px: 2 }}>
            {formattedMetaData}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('type.alarm.drawer.no_metadata', {
              ns: 'alarms',
              defaultValue: 'No metadata available.',
            })}
          </Typography>
        )}
      </InsetDrawer>
    </>
  );
};

export default AlarmMetaDataDrawer;
